import { useState, useCallback, useEffect } from "react";
import { type Settings, DEFAULT_SETTINGS } from "../types/settings";
import { mergeStoredSettings } from "../utils/settingsPersistence";

const STORAGE_KEY = "dashboard_settings_v1";

const canLoadStoredSettings = (version: number | undefined) =>
  version === undefined || version <= DEFAULT_SETTINGS.version;

export const useSettings = () => {
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);

  // Load settings from localStorage on mount
  useEffect(() => {
    const loadSettings = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored) as Settings;
          // Validate version for future migrations
          if (canLoadStoredSettings(parsed.version)) {
            setSettings(mergeStoredSettings(parsed));
          } else {
            // Handle future migrations here
            setSettings(DEFAULT_SETTINGS);
          }
        }
      } catch (error) {
        console.error("Failed to load settings:", error);
        setSettings(DEFAULT_SETTINGS);
      }
    };

    loadSettings();
  }, []);

  const getSettings = useCallback((): Settings => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Settings;
        if (canLoadStoredSettings(parsed.version)) {
          return mergeStoredSettings(parsed);
        }
      }
    } catch (error) {
      console.error("Failed to get settings:", error);
    }
    return DEFAULT_SETTINGS;
  }, []);

  const saveSettings = useCallback((newSettings: Settings) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings));
      setSettings(newSettings);
      return true;
    } catch (error) {
      console.error("Failed to save settings:", error);
      return false;
    }
  }, []);

  const resetSettings = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setSettings(DEFAULT_SETTINGS);
      return true;
    } catch (error) {
      console.error("Failed to reset settings:", error);
      return false;
    }
  }, []);

  return {
    settings,
    getSettings,
    saveSettings,
    resetSettings,
  };
};
